export const CONSTANTS = {
  "ai": {
    "rewriteConnectors": [
      "I once perceived it as {original}. I was mistaken. It was always the beginning of {new}.",
      "The memory of {original} shifts, twisting itself to make room for the truth: {new}.",
      "It was never about {original}. That was a distraction from the fact that {new}.",
      "My story changes. What I called \"{original}\" was just a shadow cast by the approach of {new}."
    ]
  },
  "aiNarrator": {
    "sentenceStarters": {
      "toReplace": [
        "Again", "Once more", "Then", "Next", "So", "And so", "But"
      ],
      "prefixes": [
        "The feeling returned:", "It happened once more;", "A familiar dread washed over me as", "The house reminded me that"
      ],
      "prefixChance": 0.4
    },
    "connectors": {
      "standard": [
        "and", "while", "as", "which meant", "because", "and in that moment,", "a fact that led to"
      ]
    },
    "sentenceComplexity": {
      "breakChance": 0.4,
      "maxClauses": 3
    },
    "horrorVocabulary": {
      "chance": 0.25,
      "replacements": {
        "walked": {
          "low": ["walked"],
          "medium": ["paced", "strode"],
          "high": ["shambled", "shuffled", "crawled"]
        },
        "saw": {
          "low": ["saw", "noticed"],
          "medium": ["glimpsed", "witnessed"],
          "high": ["beheld a vision of", "perceived"]
        },
        "dark": {
          "low": ["dark", "dim"],
          "medium": ["shadowy", "gloomy"],
          "high": ["stygian", "abyssal", "oppressive"]
        },
        "house": {
          "low": ["house"],
          "medium": ["structure", "building"],
          "high": ["entity", "presence", "carcass"]
        },
        "door": {
          "low": ["door"],
          "medium": ["portal", "entryway"],
          "high": ["maw", "wound", "gash"]
        },
        "sound": {
          "low": ["sound"],
          "medium": ["noise", "echo"],
          "high": ["cacophony", "wail", "skittering"]
        },
        "scared": {
          "low": ["scared", "afraid"],
          "medium": ["terrified", "frightened"],
          "high": ["paralyzed by fear", "gripped by terror"]
        },
        "cold": {
          "low": ["cold", "chilly"],
          "medium": ["frigid", "icy"],
          "high": ["sepulchral", "deathly cold"]
        }
      }
    },
    "phraseReplacements": {
      "in the dark": ["within the gloom", "swallowed by shadows", "where light died"],
      "i felt a chill": ["a cold dread trickled down my spine", "an unnatural cold permeated the air", "the temperature dropped inexplicably"],
      "i was scared": ["fear became a physical thing", "a primal terror seized me", "my own heartbeat was a drum of dread"]
    }
  },
  "animatedLogo": {
    "glitchDuration": 150,
    "glitchMaxInterval": 25000,
    "glitchMinInterval": 5000,
    "maxInterval": 15000,
    "minInterval": 8000,
    "transitionDuration": 4000,
    "variants": [
      "titleNormal",
      "titleFlicker",
      "titleShadow",
      "titleDistorted",
      "titleMissingLetter",
      "titleInverted",
      "titleCracked",
      "titleBleeding"
    ]
  },
  "backgroundEvents": {
    "breathing_walls": {
      "filter": "contrast(1.2) brightness(0.95)",
      "reverseFilter": "contrast(1) brightness(1)",
      "type": "filter"
    },
    "brittle_refrigerator": {
      "asset": "brittleRefrigerator",
      "position": {
        "height": "200px",
        "right": "15%",
        "top": "45%",
        "width": "120px"
      }
    },
    "creaking_floorboards": {
      "filter": "brightness(0.9) sepia(0.1)",
      "reverseFilter": "brightness(1) sepia(0)",
      "type": "filter"
    },
    "dripping_faucet": {
      "asset": "drippingFaucet",
      "position": {
        "height": "40px",
        "right": "10%",
        "top": "40%",
        "width": "30px"
      }
    },
    "faint_silhouette_stairs": {
      "asset": "faintSilhouette",
      "position": {
        "bottom": "10%",
        "height": "180px",
        "left": "50%",
        "transform": "translateX(-50%)",
        "width": "60px"
      }
    },
    "mirror_distortion": {
      "filter": "hue-rotate(10deg) contrast(1.1)",
      "reverseFilter": "hue-rotate(0deg) contrast(1)",
      "type": "filter"
    },
    "moving_shadows_photo": {
      "asset": "movingShadows",
      "position": {
        "height": "40%",
        "left": "30%",
        "top": "30%",
        "width": "40%"
      }
    },
    "portrait_eyes": {
      "asset": "portraitEyes",
      "position": {
        "height": "40px",
        "left": "25%",
        "top": "25%",
        "width": "60px"
      }
    },
    "shadow_movement": {
      "asset": "movingShadows",
      "position": {
        "bottom": "0%",
        "height": "30%",
        "left": "0%",
        "width": "100%"
      }
    },
    "shadowy_face_reflection": {
      "asset": "shadowyFace",
      "position": {
        "height": "120px",
        "left": "10%",
        "top": "20%",
        "width": "80px"
      }
    },
    "wall_whispers": {
      "filter": "brightness(1.05) saturate(0.95)",
      "reverseFilter": "brightness(1) saturate(1)",
      "type": "filter"
    },
    "watching_presence": {
      "asset": "watchingEyes",
      "position": {
        "height": "20px",
        "right": "20%",
        "top": "15%",
        "width": "40px"
      }
    }
  },
  "backgroundNarratives": {
    "dampCorridors": [
      "The air is thick with the smell of wet stone and decay.",
      "Water drips from the ceiling, a constant, maddening rhythm.",
      "This place feels ancient, forgotten."
    ],
    "mirrorHallway": [
      "My reflection fractures into a thousand pieces. Which one is me?",
      "The mirrors show not what is, but what could be, or what was.",
      "I see movement in the glass, but there's nothing behind me."
    ],
    "basementPortraits": [
      "The eyes in the portraits follow me. They know.",
      "These faces... they are all mine, twisted by time and fear.",
      "A family I don't remember, in a past that feels stolen."
    ],
    "realityShiftsRoom": [
      "The geometry of this room is a lie.",
      "The corners don't meet right. The angles are wrong.",
      "I feel dizzy, as if the floor is tilting beneath my feet."
    ],
    "redSkyRoom": [
      "The sky outside is a wound, bleeding across the horizon.",
      "That is not our sun. That is not our sky.",
      "The red light paints everything in blood."
    ],
    "shiftingHallways": [
      "I just came from this direction. How am I back here?",
      "The house is playing games with me, rearranging itself when I'm not looking.",
      "Every door leads back to the same place."
    ],
    "enormousMoonRoom": [
      "The moon is too big. It's watching me.",
      "It fills the window, a dead eye in the sky.",
      "The moonlight feels cold, heavy."
    ]
  },
  "chapterGates": {
    "A Stranger in the Photos": 8,
    "Harder to Ignore": 3,
    "Reality Shifts": 2,
    "The Guest": 5,
    "Watching While Sleeping": 6,
    "Wife's Warning": 1,
    "Will Keep Going Down": 7,
    "Wrong Lipstick Marks": 4
  },
  "combinations": {
    "A Desperate Search for Water|Damp Corridors": {
      "background": "enormousMoonRoom",
      "narrative": "My thirst leads me to a room with a single, vast window. Outside, an enormous, pale moon hangs in the sky, offering no comfort, no water, only a cold, silent judgment.",
      "type": "environment_change"
    },
    "Amnesia|Endless Staircase": {
      "id": "true_ending",
      "narrative": "At the bottom of the endless stairs, with no memory of who I was, I find the only thing left. The truth. The house is not a place. It is a memory I am trapped inside. And I am the one keeping myself here.",
      "result": "The Final Truth",
      "type": "special_event"
    },
    "Amnesia|House": {
      "narrative": "I look around the house, but nothing feels like mine. It's a stranger's home, and I am the ghost.",
      "result": "Stranger's Home",
      "type": "element"
    },
    "Amnesia|Old Photograph": {
      "narrative": "I don't recognize the person in this photo, but their eyes... they feel familiar. A ghost of a memory.",
      "result": "A Familiar Face",
      "type": "element"
    },
    "Amnesia|Stranger's Home": {
      "narrative": "If this home is not mine, and my memory is gone, then who am I? The question hangs in the air, a silent accusation.",
      "result": "Who Am I?",
      "type": "element"
    },
    "Basement Portraits|House": {
      "narrative": "In one, my wife's hair was longer; in another, I wore clothes I had never owned.",
      "result": "Not Exactly Us",
      "type": "element"
    },
    "Bathroom Door|Night": {
      "narrative": "Once... it was in the bedroom doorway.",
      "result": "Bedroom Doorway",
      "type": "element"
    },
    "Bedroom Doorway|The Guest": {
      "narrative": "Watching me while I slept.",
      "result": "Watching While Sleeping",
      "type": "element"
    },
    "Beige Wall|House": {
      "narrative": "My bank account had 100 pesos less.",
      "result": "Missing Money",
      "type": "element"
    },
    "Black Eyes|Different Versions": {
      "narrative": "One reflection moved without me... and smiled.",
      "result": "Moving Reflection",
      "type": "element"
    },
    "Blinding Light Below|Foundation": {
      "narrative": "Before I could see what was at the bottom, something yanked my shoulder and\u2014I was in bed again.",
      "result": "Yanked Back",
      "type": "element"
    },
    "Breathing Walls|House": {
      "narrative": "The house is no longer just a structure. It feels alive, its geometry shifting with each breath it takes.",
      "result": "Living Architecture",
      "type": "element"
    },
    "Cold Draft|Heavy Silence": {
      "narrative": "The draft isn't just cold; it carries a weight, a pressure that makes the silence feel like it's about to shatter.",
      "result": "A Chill That Bites",
      "type": "element"
    },
    "Confession in Every Breath|Heavy Silence": {
      "narrative": "Every creak in the wood sounded like footsteps stopping just when I listened.",
      "result": "Confession in Every Breath",
      "type": "element"
    },
    "Corrosive Spores|Locked Doors": {
      "narrative": "The spores settle on a rusted lock. The metal groans and flakes away, weakened by the unnatural decay.",
      "result": "Rusted Lock",
      "type": "element"
    },
    "Crooked Wedding Photo|Not Exactly Us": {
      "narrative": "Our smiles looked forced, as if we knew something I couldn't remember.",
      "result": "Forced Smiles",
      "type": "element"
    },
    "Curiosity Becomes Hunger|The Door That Wasn't There": {
      "narrative": "Another night, I couldn't resist. The door was still there. I opened it and descended into the basement below.",
      "result": "First Descent",
      "type": "element"
    },
    "Damp Corridors|Expired Food": {
      "narrative": "A strange, sweet smell of decay links the dampness of the corridors to the rot in the kitchen. A moldy growth spreads on the walls.",
      "result": "Moldy Growth",
      "type": "element"
    },
    "Damp Corridors|House": {
      "narrative": "Locked doors lined the corridors, each one hiding secrets I wasn't ready to face.",
      "result": "Locked Doors",
      "type": "element"
    },
    "Darkness Moves|Something is Listening": {
      "narrative": "The darkness is not just moving, it's listening. The house is not just a place, it's a presence. And it is aware of me.",
      "result": "The House is Aware",
      "type": "element"
    },
    "Days Blur|Foundation": {
      "narrative": "Sometimes, when I opened the fridge, I found food with expiration dates from over 10 years ago... even though I'd bought it that week.",
      "result": "Expired Food",
      "type": "element"
    },
    "Deep Whisper|Reality Shifts": {
      "narrative": "When I returned home, our wedding photo was crooked\u2014not the frame, but us in the picture.",
      "result": "Crooked Wedding Photo",
      "type": "element"
    },
    "Different Versions|Basement Portraits": {
      "narrative": "Older, injured, sometimes with details that made no sense.",
      "result": "Older Injured Self",
      "type": "element"
    },
    "Door Moves|House": {
      "narrative": "One night it was next to the kitchen.",
      "result": "Kitchen Door",
      "type": "element"
    },
    "Door Will Keep Appearing|First Descent": {
      "narrative": "And I will keep going down into the basement depths.",
      "result": "Will Keep Going Down",
      "type": "element"
    },
    "Doppelganger|House": {
      "background": "shiftingHallways",
      "narrative": "The house itself seems to reject the doppelganger's presence. Hallways twist into impossible shapes, rooms lead back to themselves. This is the wrong house.",
      "type": "environment_change"
    },
    "Doppelganger|Stolen Identity": {
      "narrative": "The face in the mirror is mine, but it is not me. It smiles when I do not. It knows something I don't. It is waiting to take my place.",
      "result": "Not Me",
      "type": "element"
    },
    "Echoing Mockery|Reality Shifts": {
      "background": "redSkyRoom",
      "narrative": "The mockery from the walls intensifies until the world outside the window fractures. The sky turns a deep, angry red. This is not my world anymore.",
      "type": "environment_change"
    },
    "Empty Pipes|Thirst": {
      "narrative": "The thirst is maddening, and the house offers no relief. The pipes are dry, carrying only dust and echoes.",
      "result": "A Desperate Search for Water",
      "type": "element"
    },
    "Enormous Moon|The Door That Wasn't There": {
      "narrative": "The door began appearing in different places in the house.",
      "result": "Door Moves",
      "type": "element"
    },
    "Expired Food|House": {
      "narrative": "Other times, the windows showed a red sky outside the house.",
      "result": "Red Sky",
      "type": "element"
    },
    "Faded Inscription|Memory": {
      "narrative": "The words trigger a cascade of forgetting. My own name feels foreign on my tongue. Who am I?",
      "result": "Amnesia",
      "type": "element"
    },
    "Faded Memory|Night": {
      "narrative": "In the dead of night, the faded memory becomes sharp. It guides me to a part of the house I've never seen, a room that feels like it has been forgotten by time itself.",
      "result": "Forgotten Room",
      "type": "element"
    },
    "Figure at Bottom|Wife Gone": {
      "narrative": "At first, I thought it was my wife\u2014but her skin was pale like wax.",
      "result": "Wax Pale Skin",
      "type": "element"
    },
    "First Descent|Heavy Silence": {
      "background": "dampCorridors",
      "narrative": "The silence deepens, and the air grows cold and damp. The room shifts, the walls now slick with moisture. I am in a different place.",
      "type": "environment_change"
    },
    "First Descent|House": {
      "narrative": "A staircase led down into a blinding light. I started to descend into the underground chambers.",
      "result": "Blinding Light Below",
      "type": "element"
    },
    "First Descent|Voices in the Walls": {
      "duration": 5000,
      "id": "faint_silhouette_stairs",
      "narrative": "As I listen to the whispers, a faint silhouette flickers at the bottom of the stairs, just at the edge of vision.",
      "type": "background_event"
    },
    "Footsteps Behind|Moving Reflection": {
      "narrative": "Or at least, that's what I thought\u2014until I saw a silhouette walking next to my shadow.",
      "result": "Silhouette Walking",
      "type": "element"
    },
    "Forced Smiles|House": {
      "narrative": "Our dog no longer barked... he just stared at me whenever I passed by the door.",
      "result": "Silent Dog",
      "type": "element"
    },
    "Forgotten Room|Night": {
      "background": "basementPortraits",
      "narrative": "The forgotten room is filled with portraits. In the dark, their eyes follow me. They are all of me, but older, more afraid.",
      "type": "environment_change"
    },
    "Foundation|House": {
      "narrative": "The discovery hit us like a physical blow. There was something beneath us, inaccessible from anywhere we could see.",
      "result": "Hidden Floor",
      "type": "element"
    },
    "Foundation|Night": {
      "narrative": "At night, a cold draft seeps from the floorboards, a chill that has nothing to do with the temperature.",
      "result": "Cold Draft",
      "type": "element"
    },
    "Foundation|Shadow Companion": {
      "duration": "permanent",
      "id": "shadow_movement",
      "narrative": "The shadows in the room move independently, stretching and contracting with their own rhythm.",
      "type": "background_event"
    },
    "Foundation|Thirst": {
      "narrative": "The foundation feels damp to the touch, a cold sweat on the stone. The house itself is thirsty.",
      "result": "Damp Stone",
      "type": "element"
    },
    "Foundation|Voices in the Walls": {
      "narrative": "Other times, they were muffled sounds from below, as if something was moving in the hidden floor.",
      "result": "Muffled Sounds Below",
      "type": "element"
    },
    "Ghostly Memory|House": {
      "narrative": "The memory leads me to a loose floorboard. Underneath, a faded inscription: \"She was never real\".",
      "result": "Faded Inscription",
      "type": "element"
    },
    "Hallway of Broken Mirrors|Night": {
      "duration": 4000,
      "id": "shadowy_face_reflection",
      "narrative": "In the dark, for a moment, the face in the mirror is not my own. It is gaunt, hungry.",
      "type": "background_event"
    },
    "Hallway of Broken Mirrors|Not Exactly Us": {
      "narrative": "In each shard, I saw a different version of myself reflected back.",
      "result": "Different Versions",
      "type": "element"
    },
    "Hallway of Broken Mirrors|Old Photograph": {
      "narrative": "A shard of a broken mirror reflects the old photograph. For a second, a figure in the photo turns its head to look at me.",
      "result": "Reflected Figure",
      "type": "element"
    },
    "Hallway of Broken Mirrors|The Guest": {
      "background": "realityShiftsRoom",
      "narrative": "As The Guest passes the broken mirrors, each shard reflects a different, horrifying reality. The hallway itself seems to twist and warp in response.",
      "type": "environment_change"
    },
    "Harder to Ignore|Damp Corridors": {
      "narrative": "One night, I found a hallway full of broken mirrors in the basement depths.",
      "result": "Hallway of Broken Mirrors",
      "type": "element"
    },
    "Heavy Silence|The Door That Wasn't There": {
      "id": "door_ajar",
      "narrative": "The silence in the house becomes so profound that the sound of the mysterious door creaking slightly ajar is deafening. It invites you in.",
      "type": "special_event"
    },
    "Heavy Silence|Thirst": {
      "narrative": "Every creak in the wood sounded like footsteps stopping just when I listened.",
      "result": "Confession in Every Breath",
      "type": "element"
    },
    "Hidden Floor|Night": {
      "narrative": "From that day on, the nights were filled with voices. Sometimes faint, like whispers inside the walls.",
      "result": "Voices in the Walls",
      "type": "element"
    },
    "Hollow Eyes|Figure at Bottom": {
      "narrative": "Her mouth... far too wide to be human.",
      "result": "Too Wide Mouth",
      "type": "element"
    },
    "House|Heavy Silence": {
      "duration": "permanent",
      "id": "brittle_refrigerator",
      "narrative": "The silence of the house seems to have made things brittle, fragile. The refrigerator groans, its plastic yellowed and cracked.",
      "type": "background_event"
    },
    "House|Late Night Kitchen": {
      "narrative": "A door next to the refrigerator, in a spot I'd looked at hundreds of times before. It had never been there.",
      "result": "The Door That Wasn't There",
      "type": "element"
    },
    "House|Moving Reflection": {
      "duration": 6000,
      "id": "mirror_distortion",
      "narrative": "The reflective surfaces in the room ripple like water, showing glimpses of rooms that shouldn't exist.",
      "type": "background_event"
    },
    "House|Old Photograph": {
      "narrative": "Staring at the old photo within the house, a memory surfaces, hazy and indistinct. The house in the picture is not quite this house. A window is missing, a door is in the wrong place.",
      "result": "Faded Memory",
      "type": "element"
    },
    "House|Thirst": {
      "narrative": "The thirst is a constant presence. I check the taps, but the pipes only shudder and groan, delivering nothing but air.",
      "result": "Empty Pipes",
      "type": "element"
    },
    "House|Voices in the Walls": {
      "duration": 7000,
      "id": "wall_whispers",
      "narrative": "The walls themselves seem to whisper, their surface rippling with barely audible voices.",
      "type": "background_event"
    },
    "House|Wife's Warning": {
      "narrative": "Her warning lingers in the air, thick like the scent of her perfume, though she's been gone for days.",
      "result": "A Lingering Scent",
      "type": "element"
    },
    "Kitchen Door|Door Moves": {
      "narrative": "Another, in the bathroom.",
      "result": "Bathroom Door",
      "type": "element"
    },
    "Living Architecture|The House is Aware": {
      "narrative": "The house is alive, and I am its prisoner. The walls breathe around me, a constant reminder of my confinement.",
      "result": "Sentient Prison",
      "type": "element"
    },
    "Locked Doors|Foundation": {
      "narrative": "And portraits\u2014portraits of us hanging in the basement. But they weren't exactly us.",
      "result": "Basement Portraits",
      "type": "element"
    },
    "Longer Descents|Foundation": {
      "narrative": "The hallways shift when I'm not looking. I found myself in a new location.",
      "result": "Shifting Hallways",
      "type": "element"
    },
    "Lost Count|Damp Corridors": {
      "narrative": "Each descent is longer than the last.",
      "result": "Longer Descents",
      "type": "element"
    },
    "Missing Money|Subtle Changes": {
      "narrative": "The television... was it always this small?",
      "result": "Smaller Television",
      "type": "element"
    },
    "Missing Toothbrush|Wife Gone": {
      "narrative": "But on the dining table, there were two cups of coffee, still steaming.",
      "result": "Two Coffee Cups",
      "type": "element"
    },
    "Moldy Growth|Rust and Wet Soil": {
      "narrative": "The mold feeds on the rust, producing spores that seem to eat at the very air, making it thin and sharp.",
      "result": "Corrosive Spores",
      "type": "element"
    },
    "Moving Reflection|House": {
      "narrative": "When I woke up in bed, my wife was gone.",
      "result": "Wife Gone",
      "type": "element"
    },
    "Moving Reflection|Voices Call Name": {
      "narrative": "My reflection's mouth moves, forming my name, but the sound comes from the walls around me. An echoing mockery.",
      "result": "Echoing Mockery",
      "type": "element"
    },
    "Muffled Sounds Below|Night": {
      "narrative": "When the voices stopped, the silence was worse\u2014so heavy that every breath felt like a confession.",
      "result": "Heavy Silence",
      "type": "element"
    },
    "Never Go Back Up|Wrong Lipstick Marks": {
      "narrative": "Something else was living in our house now, wearing familiar faces.",
      "result": "The Guest",
      "type": "element"
    },
    "Nightly Returns|Foundation": {
      "narrative": "Beneath the house was no ordinary basement: there were damp corridors stretching into darkness.",
      "result": "Damp Corridors",
      "type": "element"
    },
    "Night|Basement Portraits": {
      "duration": 5000,
      "id": "portrait_eyes",
      "narrative": "For a moment, the eyes in the family photos seem to track my movement across the room.",
      "type": "background_event"
    },
    "Night|Old Photograph": {
      "duration": 6000,
      "id": "moving_shadows_photo",
      "narrative": "Staring at the photograph in the dim light, the figures within seem to shift, their shadows elongating and twisting.",
      "type": "background_event"
    },
    "Night|Shadow Companion": {
      "narrative": "At night, my companion grows bolder. It is not just a shadow, but a piece of the darkness itself, moving with purpose.",
      "result": "Darkness Moves",
      "type": "element"
    },
    "Night|Something is Listening": {
      "duration": 4000,
      "id": "breathing_walls",
      "narrative": "The silence is broken by a soft, rhythmic pulsing. It's the sound of the walls breathing.",
      "type": "background_event"
    },
    "Night|Subtle Changes": {
      "narrative": "At night, the changes are more pronounced. Objects I leave on a table are on the floor by morning. Things are misplaced.",
      "result": "Misplaced Objects",
      "type": "element"
    },
    "Night|The Guest": {
      "duration": 8000,
      "id": "watching_presence",
      "narrative": "Something watches from the corners of the room, just beyond the reach of the light.",
      "type": "background_event"
    },
    "Night|Thirst": {
      "narrative": "At exactly 2:57 a.m., I woke up thirsty and went downstairs to get some water. That's when I saw it.",
      "result": "Late Night Kitchen",
      "type": "element"
    },
    "Night|Will Keep Going Down": {
      "narrative": "The descent never ends. The stairs stretch into an impossible darkness, twisting back on themselves. There is no bottom.",
      "result": "Endless Staircase",
      "type": "element"
    },
    "No Longer Belong|The Door That Wasn't There": {
      "narrative": "All I know is that the door will keep appearing...",
      "result": "Door Will Keep Appearing",
      "type": "element"
    },
    "Not Exactly Us|Damp Corridors": {
      "narrative": "The air smelled of rust and wet soil, like something had been buried and forgotten.",
      "result": "Rust and Wet Soil",
      "type": "element"
    },
    "Not Me|Forced Smiles": {
      "narrative": "The thing wearing my face smiles with my mouth, but its eyes are cold. It is a replacement, and I am being erased.",
      "result": "The Replacement",
      "type": "element"
    },
    "Old Photograph|Voices in the Walls": {
      "narrative": "As the whispers from the walls grow louder, a memory surfaces, not mine, but from the photograph. A moment of joy, now tainted with dread.",
      "result": "Ghostly Memory",
      "type": "element"
    },
    "Older Injured Self|Hallway of Broken Mirrors": {
      "narrative": "Sometimes with pitch-black eyes that reflected nothing.",
      "result": "Black Eyes",
      "type": "element"
    },
    "Reality Shifts|First Descent": {
      "narrative": "Night after night, I kept going back in. Each time, I remembered a little more of the basement.",
      "result": "Nightly Returns",
      "type": "element"
    },
    "Red Sky|Night": {
      "narrative": "And an enormous moon\u2014even in the middle of the day.",
      "result": "Enormous Moon",
      "type": "element"
    },
    "Reflected Figure|The Guest": {
      "narrative": "The figure in the reflection wears the face of The Guest. It seems the house doesn't just create things, it replaces them.",
      "result": "Stolen Identity",
      "type": "element"
    },
    "Rooms Not Where They Should Be|Voices Call Name": {
      "narrative": "Sometimes, I hear footsteps behind me... but when I turn around, there's no one there.",
      "result": "Footsteps Behind",
      "type": "element"
    },
    "Rust and Wet Soil|Basement Portraits": {
      "narrative": "And always, from somewhere deep within, a whisper: \"You haven't seen it all yet.\"",
      "result": "Deep Whisper",
      "type": "element"
    },
    "Sentient Prison|The Guest": {
      "background": "shiftingHallways",
      "narrative": "The Guest seems at home in this impossible place. The walls shift to accommodate it, or perhaps to trap me with it. There is no escape.",
      "type": "environment_change"
    },
    "Shadow Companion|Lost Count": {
      "narrative": "I don't know if the house is changing... or if I'm the one who no longer belongs in this version of it.",
      "result": "No Longer Belong",
      "type": "element"
    },
    "Shadow Companion|Not Exactly Us": {
      "narrative": "My shadow stretches towards the portraits on the wall, merging with the painted shadows until they are indistinguishable.",
      "result": "Distorted Family Portrait",
      "type": "element"
    },
    "Shifting Hallways|Basement Portraits": {
      "narrative": "And the rooms aren't where they should be.",
      "result": "Rooms Not Where They Should Be",
      "type": "element"
    },
    "Silent Dog|The Door That Wasn't There": {
      "narrative": "The changes became harder to ignore with each descent into the basement.",
      "result": "Harder to Ignore",
      "type": "element"
    },
    "Silhouette Walking|The Guest": {
      "narrative": "Something walks with me now, matching my every step.",
      "result": "Shadow Companion",
      "type": "element"
    },
    "Smaller Television|The Door That Wasn't There": {
      "narrative": "I told myself I was imagining things. But deep down, I knew these changes were tied to that door. The environment shifted subtly.",
      "result": "Reality Shifts",
      "type": "element"
    },
    "Something is Listening|Night": {
      "duration": 4000,
      "id": "breathing_walls",
      "narrative": "The silence is broken by a soft, rhythmic pulsing. It's the sound of the walls breathing.",
      "result": "Breathing Walls",
      "type": "element"
    },
    "Stolen Identity|Night": {
      "narrative": "I see it in the corner of my eye. A figure that looks just like me, but its movements are wrong, too sharp. A doppelganger.",
      "result": "Doppelganger",
      "type": "element"
    },
    "Subtle Changes|Foundation": {
      "narrative": "The living room wall was beige when I swore it had always been white.",
      "result": "Beige Wall",
      "type": "element"
    },
    "The Door That Wasn't There|Night": {
      "narrative": "We went back to bed. But from that night on, my curiosity became hunger.",
      "result": "Curiosity Becomes Hunger",
      "type": "element"
    },
    "The Door That Wasn't There|Voices in the Walls": {
      "narrative": "Before I could open it, a hand gripped my arm\u2014it was my wife. \"Don't,\" she whispered. \"If you go in... it will only get worse.\"",
      "result": "Wife's Warning",
      "type": "element"
    },
    "The Guest|House": {
      "narrative": "The days began to blur together, reality becoming fluid.",
      "result": "Days Blur",
      "type": "element"
    },
    "The Guest|Old Photograph": {
      "narrative": "I look at the photograph again. In the background, behind my smiling family, stands a figure I've never noticed before. It has the same pale face as The Guest.",
      "result": "A Stranger in the Photos",
      "type": "element"
    },
    "The Guest|Silent Dog": {
      "narrative": "The dog no longer stares. It whimpers softly when the Guest is near, hiding under the furniture.",
      "result": "A Whimper in the Dark",
      "type": "element"
    },
    "The Guest|Two Coffee Cups": {
      "narrative": "There are two cups, but three of us are here now. The math doesn't add up. A third presence makes itself known.",
      "result": "A Third Presence",
      "type": "element"
    },
    "Thirst|Late Night Kitchen": {
      "duration": "permanent",
      "id": "dripping_faucet",
      "narrative": "The kitchen faucet drips steadily, each drop echoing like footsteps in the silence.",
      "type": "background_event"
    },
    "Too Wide Mouth|Voices Call Name": {
      "narrative": "She spoke with my wife's voice, but the words were wrong.",
      "result": "Wife's Voice",
      "type": "element"
    },
    "Two Coffee Cups|Moving Reflection": {
      "narrative": "The second cup had lipstick marks... but they weren't hers.",
      "result": "Wrong Lipstick Marks",
      "type": "element"
    },
    "Voices Call Name|First Descent": {
      "narrative": "One night, descending the stairs, I saw a figure at the bottom of the basement.",
      "result": "Figure at Bottom",
      "type": "element"
    },
    "Voices in the Walls|Heavy Silence": {
      "narrative": "The silence is so deep it has a weight. And in that weight, I realize the whispers from the walls have stopped because they are no longer trying to get in. They are listening from inside.",
      "result": "Something is Listening",
      "type": "element"
    },
    "Watching While Sleeping|First Descent": {
      "narrative": "I've lost track of how many times I've gone down there into the basement.",
      "result": "Lost Count",
      "type": "element"
    },
    "Wax Pale Skin|Moving Reflection": {
      "narrative": "Her eyes hollow, empty sockets that seemed to drink in the light.",
      "result": "Hollow Eyes",
      "type": "element"
    },
    "Wife Gone|Foundation": {
      "narrative": "Her toothbrush was missing. Her clothes were gone.",
      "result": "Missing Toothbrush",
      "type": "element"
    },
    "Wife's Voice|Wax Pale Skin": {
      "narrative": "\"Don't you realize you never go back up?\" I woke up. Or at least... I think I did.",
      "result": "Never Go Back Up",
      "type": "element"
    },
    "Wrong Lipstick Marks|Voices in the Walls": {
      "narrative": "The voices began to call my name. Not whispers anymore, but clear... as if someone was speaking directly into my ear.",
      "result": "Voices Call Name",
      "type": "element"
    },
    "Yanked Back|House": {
      "narrative": "I couldn't remember what happened, but subtle details had changed in the house above.",
      "result": "Subtle Changes",
      "type": "element"
    },
    "Amnesia|The Guest": {
      "narrative": "The Guest looks at me with my own eyes. It knows my name, even when I do not. Are we one and the same?",
      "result": "Shared Identity",
      "type": "element"
    },
    "Shadow Companion|Foundation": {
      "narrative": "The shadow doesn't come from the light. It seeps up from the floorboards, from the foundation. It belongs to the house, not to me.",
      "result": "Shadow from Below",
      "type": "element"
    },
    "Endless Staircase|The Guest": {
      "narrative": "I see The Guest on the stairs below me, beckoning. It doesn't walk down; it simply appears on a lower step, waiting.",
      "result": "An Invitation Downward",
      "type": "element"
    },
    "Sentient Prison|Amnesia": {
      "narrative": "The house holds me, but it also holds my memories. It won't let me leave because it won't let me remember who I am outside of its walls.",
      "result": "The House Remembers",
      "type": "element"
    },
    "A Familiar Face|Wife's Warning": {
      "narrative": "The familiar face in the photo, the one I almost recognize, seems to mouth the same warning my wife gave me. 'Don't.' A memory that is not mine, a warning from a stranger who is myself.",
      "result": "A Stranger's Warning",
      "type": "element"
    },
    "The House is Aware|The Guest": {
      "narrative": "The house is aware, and The Guest is its agent. It moves through the shifting corridors with a purpose I can't fathom. It is not a prisoner here. It is a warden.",
      "result": "The Warden",
      "type": "element"
    }
  },
  "realityDrift": {
    "max": 100,
    "failGain": 3,
    "inactivityGain": 5,
    "inactivityInterval": 18000,
    "newDiscoveryLoss": -10,
    "oldDiscoveryLoss": -1,
    "backgroundChangeChanceOnCombine": 0.05,
    "unsettlingBackgrounds": [
      "dampCorridors",
      "basementPortraits",
      "mirrorHallway",
      "realityShiftsRoom",
      "redSkyRoom",
      "shiftingHallways",
      "enormousMoonRoom"
    ],
    "elementMovement": {
      "activationThreshold": 75,
      "intervalMin": 8000,
      "intervalMax": 12000,
      "preMoveGlowDuration": 1500,
      "anchorDuration": 15000,
      "moveDistance": 50
    }
  },
  "doorTransition": {
    "doorAppearDelay": 500,
    "doorOpenDelay": 2000,
    "fadeOutDuration": 1500,
    "totalDuration": 6000,
    "zoomInDelay": 1500
  },
  "dragBoundary": {
    "elementSize": 100,
    "fullScene": {
      "bottom": 600,
      "left": 0,
      "right": 800,
      "top": 0
    }
  },
  "dynamicBackground": {
    "intrusions": {
      "chanceOnCombine": 0.15,
      "chanceOnCorruptionTick": 0.2,
      "clickFailNarrative": "I blink, and it's gone. Was it ever there? The doubt is a cold weight in my gut.",
      "clickSuccessNarrative": "I focus on it, and for a moment, it feels solid. {detail}",
      "duration": 5000,
      "list": [
        {
          "asset": "shadow",
          "id": "shadow_figure",
          "narrativeDetail": "The shadow detaches from the wall, a deeper black against the gloom.",
          "position": {
            "left": "75%",
            "top": "20%"
          },
          "size": {
            "height": 300,
            "width": 150
          }
        },
        {
          "asset": "face",
          "id": "pale_face",
          "narrativeDetail": "A pale, featureless face peers from the darkness, its expression a void.",
          "position": {
            "left": "15%",
            "top": "10%"
          },
          "size": {
            "height": 120,
            "width": 80
          }
        },
        {
          "asset": "eyes",
          "id": "glowing_eyes",
          "narrativeDetail": "Two points of faint light resolve into eyes, watching me from beneath the furniture.",
          "position": {
            "left": "50%",
            "top": "60%",
            "transform": "translateX(-50%)"
          },
          "size": {
            "height": 40,
            "width": 100
          }
        }
      ]
    },
    "jumpscares": {
      "chanceOnCritical": 0.5,
      "duration": 150,
      "list": [
        {
          "asset": "creature",
          "id": "creature_flash",
          "sound": "jumpscare1"
        },
        {
          "asset": "distorted_face",
          "id": "distorted_face",
          "sound": "jumpscare2"
        }
      ]
    },
    "subtleShifts": {
      "chanceOnChapterAdvance": 0.8,
      "chanceOnHighCorruption": 0.1,
      "fadeDuration": 8000,
      "shifts": [
        {
          "filter": "sepia(0.4) hue-rotate(-20deg)",
          "id": "color_shift_sepia",
          "narrative": "The colors in the room seem to bleed, aging before my eyes."
        },
        {
          "filter": "hue-rotate(30deg) saturate(0.8)",
          "id": "color_shift_cold",
          "narrative": "A cold, blueish tint seeps into the room, as if winter has come to the house."
        },
        {
          "filter": "brightness(0.8) contrast(1.2)",
          "id": "brightness_shift",
          "narrative": "The light feels wrong, heavier. The shadows seem to stretch and deepen."
        }
      ]
    }
  },
  "gameTitle": "Underneath the house: Fragments",
  "genre": "Psychological Horror + Narrative Puzzle",
  "initialElements": [
    "House",
    "Foundation",
    "Night",
    "Thirst"
  ],
  "intro": {
    "endingElements": [
      "House",
      "Foundation",
      "Night",
      "Old Photograph"
    ],
    "sequence": [
      {
        "backgroundImage": "introRoom",
        "duration": 5000,
        "effects": [
          "whisper"
        ],
        "text": "It's been three days since I arrived. Or was it four?"
      },
      {
        "backgroundImage": "menuHallway",
        "duration": 6000,
        "effects": [
          "doorCreak"
        ],
        "text": "The corridors don't end\u2026 doors lead to rooms that shouldn't exist."
      },
      {
        "asset": {
          "position": {
            "bottom": "15%",
            "right": "15%"
          },
          "type": "oldPhotograph"
        },
        "backgroundImage": "introRoom",
        "duration": 6000,
        "effects": [
          "whisper"
        ],
        "text": "I don't remember how I got here. My memory is a faded photograph."
      },
      {
        "backgroundImage": "introRoom",
        "cues": {
          "cold": "breath"
        },
        "duration": 5000,
        "effects": [
          "heartbeat"
        ],
        "text": "A sudden chill runs down my spine. I feel cold."
      },
      {
        "backgroundImage": "introRoom",
        "duration": 0,
        "effects": [],
        "interaction": {
          "prompt": "Click to light the candle",
          "type": "candle"
        },
        "text": "There's a candle on the floor. A single point of hope in the oppressive dark."
      },
      {
        "backgroundImage": "introRoom",
        "duration": 6000,
        "effects": [
          "flicker",
          "heartbeat"
        ],
        "text": "The flame flickers, casting dancing shadows. They writhe and twist."
      },
      {
        "backgroundImage": "introRoom",
        "duration": 7000,
        "effects": [
          "whisper",
          "heartbeat"
        ],
        "text": "For a moment, in the reflection of a dusty mirror... I see something move behind me."
      },
      {
        "backgroundImage": "introRoom",
        "duration": 6000,
        "effects": [],
        "text": "But when I turn, there is nothing. Only the silence, and the feeling of being watched."
      },
      {
        "backgroundImage": "introRoom",
        "duration": 5000,
        "effects": [],
        "text": "I must understand this place. I must remember."
      }
    ],
    "timing": {
      "candleTimeout": 8000,
      "narrativeDuration": 6000,
      "openingDuration": 4000,
      "skipButtonDelay": 3000,
      "transitionDuration": 2000
    }
  },
  "invisibleHandTexts": [
    "I didn't mean to reach for that.",
    "My hand knows this movement already.",
    "Something completes my thought without asking.",
    "I watch myself follow instructions I didn't hear.",
    "The house moves for me."
  ],
  "loreFragments": {
    "chapters": {
      "0": [
        "The house was once normal."
      ],
      "1": [
        "Don't open the door."
      ],
      "2": [
        "Each descent costs something."
      ],
      "3": [
        "The portraits aren't quite right."
      ],
      "4": [
        "The mirrors show other selves."
      ],
      "5": [
        "The guest speaks with her voice."
      ],
      "6": [
        "Reality warps around the door."
      ],
      "7": [
        "You will keep going down."
      ],
      "8": [
        "The photos are not memories. They are warnings."
      ]
    },
    "general": [
      "The door remembers being opened.",
      "Your wife's warning echoes in the silence.",
      "Something walks in your shadow now.",
      "The basement portraits watch you sleep.",
      "Reality is more fragile than you thought.",
      "The house learns your habits.",
      "Every descent changes something.",
      "The voices know your name.",
      "The mirror shards reflect other lives.",
      "The house dislikes straight lines.",
      "The wedding photo is crooked again. I just straightened it.",
      "My name sounds strange when I say it out loud now.",
      "Corners remember more than walls do.",
      "That sound... it is not just the house settling.",
      "The darkness here is not empty. It is just patient."
    ],
    "highCorruption": [
      "You never really go back up.",
      "The guest wears familiar faces.",
      "Your reflection moves independently.",
      "The house is changing you.",
      "Something else lives here now.",
      "The floorboards learn my weight and practice my footsteps.",
      "My reflection rehearses my mistakes.",
      "The air is thick with unspoken things."
    ],
    "lowCorruption": [
      "The foundation holds secrets.",
      "Night brings whispers.",
      "The door wasn't always there.",
      "A draft draws a map across my skin.",
      "The silence is a question I don't want to answer.",
      "I feel like I'm forgetting something important."
    ]
  },
  "mainMenuAnimation": {
    "doorAppearIntervalMax": 15000,
    "doorAppearIntervalMin": 8000,
    "doorVisibleDuration": 5000,
    "footstepIntervalMax": 20000,
    "footstepIntervalMin": 10000
  },
  "menu": {
    "continue": "Return to the House",
    "mainMenu": "Leave the House",
    "newGame": "Begin the Descent",
    "pause": "Pause",
    "resume": "Resume",
    "settings": "Settings",
    "subtitle": "A Narrative Horror Game",
    "title": "Underneath the House"
  },
  "narrative": {
    "hints": [
      "The door wasn't always there.",
      "Each descent changes something.",
      "The portraits aren't quite right.",
      "Your reflection doesn't always follow.",
      "The guest speaks with her voice.",
      "Reality warps around the door.",
      "Some memories don't want to be connected.",
      "Listen to the silence. It has a voice.",
      "What if the door wants me to open it?",
      "Losing my mind is a kind of progress."
    ],
    "initial": "There was a time when I had everything. The house stood around us, solid and familiar. But something changed the day we discovered the foundations weren't hollow as we'd always believed.",
    "invalidDrop": "The house doesn't recognize what you're trying to bring in. It feels... foreign."
  },
  "narrativeCues": {
    "cold draft": {
      "duration": 1500,
      "effect": "narrative_flicker"
    },
    "creak": {
      "duration": 1000,
      "effect": "narrative_creak_vibration"
    },
    "darkness": {
      "duration": 2500,
      "effect": "narrative_darkness_pulse"
    },
    "draft": {
      "duration": 1200,
      "effect": "narrative_flicker"
    },
    "eyes": {
      "duration": 2000,
      "effect": "narrative_watching_presence"
    },
    "flicker": {
      "duration": 800,
      "effect": "narrative_flicker"
    },
    "footsteps": {
      "duration": 2500,
      "effect": "narrative_footstep_echo"
    },
    "groans": {
      "duration": 1500,
      "effect": "narrative_creak_vibration"
    },
    "light": {
      "duration": 1800,
      "effect": "narrative_light_surge"
    },
    "mirror": {
      "duration": 2000,
      "effect": "narrative_mirror_ripple"
    },
    "reflection": {
      "duration": 2000,
      "effect": "narrative_mirror_ripple"
    },
    "shadow": {
      "duration": 2000,
      "effect": "narrative_shadow_glimpse"
    },
    "shadows": {
      "duration": 2000,
      "effect": "narrative_shadow_glimpse"
    },
    "silence": {
      "duration": 4000,
      "effect": "narrative_heavy_silence"
    },
    "watching": {
      "duration": 3500,
      "effect": "narrative_watching_presence"
    },
    "whisper": {
      "duration": 3000,
      "effect": "narrative_whisper_echo"
    },
    "whispers": {
      "duration": 3000,
      "effect": "narrative_whisper_echo"
    }
  },
  "narrativeEffects": {
    "narrative_creak_vibration": {
      "duration": 200,
      "pulses": 4,
      "transform": "translateX(1px)",
      "type": "transform"
    },
    "narrative_darkness_pulse": {
      "color": "#000000",
      "duration": 600,
      "opacity": 0.15,
      "pulses": 2,
      "type": "overlay"
    },
    "narrative_flicker": {
      "duration": 300,
      "filter": "brightness(1.3) contrast(1.1)",
      "pulses": 3,
      "type": "filter"
    },
    "narrative_footstep_echo": {
      "duration": 150,
      "pulses": 5,
      "transform": "translateY(-1px)",
      "type": "transform"
    },
    "narrative_heavy_silence": {
      "duration": 1000,
      "filter": "brightness(0.95) contrast(1.05)",
      "type": "filter"
    },
    "narrative_light_surge": {
      "duration": 400,
      "filter": "brightness(1.2) saturate(1.1)",
      "type": "filter"
    },
    "narrative_mirror_ripple": {
      "duration": 600,
      "filter": "hue-rotate(10deg) blur(0.5px)",
      "pulses": 2,
      "type": "filter"
    },
    "narrative_shadow_glimpse": {
      "color": "#000000",
      "duration": 500,
      "opacity": 0.2,
      "type": "overlay"
    },
    "narrative_watching_presence": {
      "color": "#330000",
      "duration": 800,
      "opacity": 0.1,
      "type": "overlay"
    },
    "narrative_whisper_echo": {
      "duration": 800,
      "filter": "hue-rotate(5deg) saturate(1.1)",
      "pulses": 2,
      "type": "filter"
    }
  },
  "narrativeTones": {
    "bands": [
      {
        "threshold": 0,
        "prefixChance": 0,
        "suffixChance": 0,
        "fragmentChance": 0
      },
      {
        "threshold": 25,
        "prefixes": [
          "I think...",
          "Maybe...",
          "It feels like..."
        ],
        "suffixes": [
          "...doesn't it?",
          "...or was it?",
          "I'm not sure."
        ],
        "prefixChance": 0.15,
        "suffixChance": 0.15,
        "fragmentChance": 0
      },
      {
        "threshold": 50,
        "prefixes": [
          "Can't escape...",
          "It's all wrong...",
          "Help me..."
        ],
        "suffixes": [
          "...it never stops.",
          "...I'm losing it.",
          "...the walls are closing in."
        ],
        "prefixChance": 0.3,
        "suffixChance": 0.3,
        "fragmentChance": 0.2
      },
      {
        "threshold": 75,
        "prefixes": [
          "gone",
          "lost",
          "endless"
        ],
        "suffixes": [
          "...",
          "why",
          "no"
        ],
        "prefixChance": 0.5,
        "suffixChance": 0.5,
        "fragmentChance": 0.4
      }
    ]
  },
  "proceduralGen": {
    "backgroundTextures": [
      "concrete",
      "wood",
      "static",
      "fabric"
    ],
    "overlays": [
      "cracks",
      "stains",
      "fog",
      "vignette",
      "blood"
    ],
    "symbols": [
      "eye",
      "hand",
      "mouth",
      "spiral",
      "face"
    ]
  },
  "tutorial": {
    "initialElements": [
      "House",
      "Foundation"
    ],
    "initialNarrative": "This is a tutorial. The story will begin when you start a new game.",
    "exampleNarrative": "The discovery hit us like a physical blow. There was something beneath us, inaccessible from anywhere we could see.",
    "steps": [
      {
        "id": "welcome",
        "title": "Welcome to the House",
        "text": "This tutorial will guide you through the basics of 'Underneath the House'. You'll learn how to discover new story fragments and interact with the world.",
        "action": "next"
      },
      {
        "id": "discoveries",
        "title": "Your Discoveries",
        "text": "This is your discoveries panel. All the concepts, memories, and objects you find will appear here. These are the building blocks of your story.",
        "highlight": "discoveriesPanel",
        "action": "next"
      },
      {
        "id": "scene",
        "title": "The Scene",
        "text": "This is the main scene. It reflects the current state of the house and your mind. You will interact with elements by bringing them into this space.",
        "highlight": "sceneArea",
        "action": "next"
      },
      {
        "id": "interaction_desktop",
        "title": "Making Combinations",
        "text": "To make a new discovery, drag two elements from your discoveries panel and drop them on top of each other in the scene.",
        "highlight": "sceneArea",
        "action": "next",
        "isDesktopOnly": true
      },
      {
        "id": "interaction_mobile",
        "title": "Making Combinations",
        "text": "On mobile, tap two elements from your discoveries. They will be added to the crafting slots below. Then, press 'Combine'.",
        "highlight": "craftingSlots",
        "action": "next",
        "isMobileOnly": true
      },
      {
        "id": "first_combination",
        "title": "Your First Combination",
        "text": "Let's try it. Combine 'House' and 'Foundation' to see what happens. This will create a new story element.",
        "highlight": "discoveriesPanel",
        "action": "combine",
        "required": [
          "House",
          "Foundation"
        ],
        "result": "Hidden Floor"
      },
      {
        "id": "narrative",
        "title": "The Story Unfolds",
        "text": "Well done. Each combination adds a new paragraph to your story. You can review the full narrative at any time by clicking the journal icon.",
        "highlight": "historyButton",
        "action": "next"
      },
      {
        "id": "new_element",
        "title": "New Discoveries",
        "text": "Your combination created 'Hidden Floor'. New discoveries like this unlock more potential combinations, allowing you to delve deeper into the mystery.",
        "highlight": "discoveriesPanel",
        "action": "next"
      },
      {
        "id": "reality_drift",
        "title": "Reality Drift",
        "text": "This meter shows your 'Reality Drift'. Failed combinations and inactivity increase it, while new discoveries lower it. If it reaches 100%, your story ends. Keep exploring to stay grounded.",
        "highlight": "driftMeter",
        "action": "next"
      },
      {
        "id": "completion",
        "title": "You Are Ready",
        "text": "You now know everything you need to begin. The house is waiting. What secrets will you uncover in its depths?",
        "action": "finish"
      }
    ]
  },
  "ui": {
    "ambientToggleOff": "Ambient: Off",
    "ambientToggleOn": "Ambient: On",
    "enlargedDisplayDuration": 400,
    "enlargedElementSize": 80,
    "hintFirstCombo": "Try combining your initial elements to begin the story.",
    "hintMobile": "Tap an element to add it to a slot, then combine.",
    "journalTitle": "Journal",
    "longPressDuration": 500
  },
  "volume": {
    "defaultMaster": 0.7,
    "defaultMusic": 0.5,
    "defaultMuted": false,
    "defaultSfx": 0.8,
    "defaultBrightness": 1
  }
}
